'use client'

import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import Image from 'next/image';
// Use the native ResizeObserver to avoid legacy JS polyfills
import { LazyMotion, domAnimation, m, useScroll, useTransform, useSpring } from "framer-motion";

type ListSet = {
  title: string;
  imageSrc: string;
}

type ContentItem = {
  list1: ListSet[],
  list2: ListSet[],
  list3: ListSet[]
}

type ImageVerticalScrollProps = {
  content: ContentItem[];
}

const ImageVerticalScroll = ({ content }: ImageVerticalScrollProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const [scrollRange, setScrollRange] = useState(0);
  const [viewportW, setViewportW] = useState(0);

  useLayoutEffect(() => {
    if (scrollRef.current) {
      setScrollRange(scrollRef.current.scrollWidth);
    }
  }, [scrollRef]);

  const onResize = useCallback((entries: ResizeObserverEntry[]) => {
    for (let entry of entries) {
      setViewportW(entry.contentRect.width);
    }
  }, []);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => onResize(entries));
    const currentGhostRef = ghostRef.current;
    if (currentGhostRef) {
      resizeObserver.observe(currentGhostRef);
    }
    return () => {
      if (currentGhostRef) {
        resizeObserver.unobserve(currentGhostRef);
      }
    };
  }, [onResize]);

  const { scrollYProgress } = useScroll();
  const transform1 = useTransform(
    scrollYProgress,
    [0, 1],
    [0, (-scrollRange + 2000) + viewportW]
  );
  const transform2 = useTransform(
    scrollYProgress,
    [1, 0],
    [0, -scrollRange + viewportW]
  );
  const physics = { damping: 30, mass: 0.20, stiffness: 2000 };
  const spring1 = useSpring(transform1, physics);
  const spring2 = useSpring(transform2, physics);

  return (
    <React.Fragment>
      <LazyMotion features={domAnimation}>
        <div className="scroll-container">
          <m.section
            ref={scrollRef}
            style={{ x: spring1 }}
            className="thumbnails-container"
          >
            <div className="thumbnails">
              {content.map((item, index) => (
                <span key={index}>
                  {item.list1.map((item, index) => (
                    <figure key={index} className="outline outline-offset-2 outline-4 outline-[var(--border-color)] hover:outline-[var(--primary-color)] rounded-xl overflow-hidden">
                      <Image
                        className="thumbnail object-cover grayscale hover:grayscale-0 w-[250px] h-[150px] md:w-[450px] md:h-[300px]"
                        width={450}
                        height={300}
                        src={item.imageSrc}
                        alt={item.title}
                        sizes="(max-width: 768px) 250px, 450px"
                        quality={70}
                      />
                    </figure>
                  ))}
                </span>
              ))}
            </div>
          </m.section>
        </div>
        <div className="scroll-container">
          <m.section
            ref={scrollRef}
            style={{ x: spring2 }}
            className="thumbnails-container"
          >
            <div className="thumbnails">
              {content.map((item, index) => (
                <span key={index}>
                  {item.list2.map((item, index) => (
                    <figure key={index} className="outline outline-offset-2 outline-4 outline-[var(--border-color)] hover:outline-[var(--primary-color)] rounded-xl overflow-hidden">
                      <Image
                        className="thumbnail object-cover grayscale hover:grayscale-0 w-[250px] h-[150px] md:w-[450px] md:h-[300px]"
                        width={450}
                        height={300}
                        src={item.imageSrc}
                        alt={item.title}
                        sizes="(max-width: 768px) 250px, 450px"
                        quality={70}
                      />
                    </figure>
                  ))}
                </span>
              ))}
            </div>
          </m.section>
        </div>
        <div className="scroll-container">
          <m.section
            ref={scrollRef}
            style={{ x: spring1 }}
            className="thumbnails-container"
          >
            <div className="thumbnails">
              {content.map((item, index) => (
                <span key={index}>
                  {item.list3.map((item, index) => (
                    <figure key={index} className="outline outline-offset-2 outline-4 outline-[var(--border-color)] hover:outline-[var(--primary-color)] rounded-xl overflow-hidden">
                      <Image
                        className="thumbnail object-cover grayscale hover:grayscale-0 w-[250px] h-[150px] md:w-[450px] md:h-[300px]"
                        width={450}
                        height={300}
                        src={item.imageSrc}
                        alt={item.title}
                        sizes="(max-width: 768px) 250px, 450px"
                        quality={70}
                      />
                    </figure>
                  ))}
                </span>
              ))}
            </div>
          </m.section>
        </div>
      </LazyMotion>
      <div ref={ghostRef} style={{ height: scrollRange }} className="ghost" />
    </React.Fragment>
  );
};

export default ImageVerticalScroll;
